import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth';
import { isAdminUser } from '../../config/environment';
import { getApiUrl } from '../../config/api';
import { 
  generateImage as apiGenerateImage, 
  GenerateImageRequest, 
  GenerateImageResponse,
  ReferenceImage,
  PROVIDER_CONFIG,
  getStylePresets,
  getMaxImages,
  getSupportedRatios,
  hasFeature,
  mapAspectRatioToRunway,
  mapRunwayRatioToStandard
} from '../../services/imageGenerationApi';
import { PromptBrowser, ReferenceImageUploader } from '../shared';
import { useReferenceImages } from '../../hooks';
import { 
  VideoTab, 
  VideoResult, 
  UpscaleTab,
  ImageGenerationTab,
  ImageResultsDisplay,
  PromptBrowserTab,
  // Import utilities
  QUALITY_PRESETS,
  STYLE_PRESETS,
  ASPECT_RATIOS,
  VIDEO_QUALITIES,
  VIDEO_DURATIONS,
  generateImage,
  generateVideo,
  buildFinalPrompt,
  mapToSDXLParams,
  getSDXLQualitySettings,
  getDimensionsForRatio,
  getQualitySettings,
  GeneratedImage,
  GeneratedVideo,
  CharacterData,
  UpscaleResult,
  AIImageModalProps,
  ImageProvider,
  VideoProvider,
  TabType,
  ImageCount,
  GenerateVideoRequest,
  GenerateVideoResponse
} from './AIGenerator';
import { getSelectedVideoImage } from './AIGenerator/videoUtils';
import VeoVideoControls from './AIGenerator/VeoVideoControls';
const AIImageModalFresh: React.FC<AIImageModalProps> = ({ 
  isOpen, 
  onClose, 
  onImageGenerated, 
  onVideoGenerated,
  onVideoUpscaled,
  projectId,
  initialTab = 'image',
  initialImageUrl,
  initialVideoUrl,
  initialPrompt,
  initialProvider,
  disableVideoTab = false,
  disablePromptsTab = false,
  disableUpscaleTab = false
}) => {
  // Translation hook
  const { t } = useTranslation('aiGenerator');
  
  // Core state
  const [prompt, setPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<string>('16:9');
  const [selectedQuality, setSelectedQuality] = useState<string>('standard');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  
  // Test generation state (admin only)
  const [isTestGenerating, setIsTestGenerating] = useState(false);
  
  // NEW: Image count selection state
  const [imageCount, setImageCount] = useState<2 | 4>(4); // Default to 4 images
  
  // NEW: Provider state (admin detection via existing auth)
  const [selectedProvider, setSelectedProvider] = useState<'runway' | 'sdxl' | 'banana_nano'>('runway');
  const { user } = useAuth();
  const isAdmin = user ? isAdminUser(user) : false;
  
  // Reference images management using custom hook
  const {
    referenceImages,
    addReferenceImage,
    addFromLibrary,
    removeReferenceImage,
    clearReferenceImages,
    canAddMore,
    maxImages
  } = useReferenceImages(3);
  
  const [referenceImageTags, setReferenceImageTags] = useState<Record<string, string>>({});
  
  // Advanced Image Controls State
  const [selectedImageStyle, setSelectedImageStyle] = useState<string>('');
  const [selectedLighting, setSelectedLighting] = useState<string>('');
  const [selectedImageQuality, setSelectedImageQuality] = useState<string>('standard');
  const [enhanceDetails, setEnhanceDetails] = useState<boolean>(false);
  const [colorBoost, setColorBoost] = useState<boolean>(false);
  const [improveComposition, setImproveComposition] = useState<boolean>(false);
  
  // Accordion state for image controls
  const [showImageStyleAccordion, setShowImageStyleAccordion] = useState<boolean>(false);
  const [showImageEnhancementAccordion, setShowImageEnhancementAccordion] = useState<boolean>(false);
  
  // Tab state - now includes 'prompt' and 'upscale' tabs
  const [activeTab, setActiveTab] = useState<'image' | 'video' | 'prompt' | 'upscale'>(initialTab as 'image' | 'video' | 'prompt' | 'upscale');
  
  // Video generation state
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoQuality, setVideoQuality] = useState<string>('standard');
  const [videoDuration, setVideoDuration] = useState<number>(5); // Default to 8s for VEO routing
  const [videoAspectRatio, setVideoAspectRatio] = useState<string>('16:9');
  const [selectedVideoProvider, setSelectedVideoProvider] = useState<'runway' | 'ltx' | 'veo'>('veo');
  
  // Veo-specific state
  const [veoModel, setVeoModel] = useState<string>('veo-3.0-generate-preview');
  const [negativePrompt, setNegativePrompt] = useState<string>('');
  const [personGeneration, setPersonGeneration] = useState<string>('allow_adult');
  const [dialogueCue, setDialogueCue] = useState<string>('');
  const [veoSoundEffects, setVeoSoundEffects] = useState<string>('');
  const [veoAmbientNoise, setVeoAmbientNoise] = useState<string>('');
  const [veoAudioStyle, setVeoAudioStyle] = useState<string>('');
  
  // Veo advanced controls
  const [veoVisualStyle, setVeoVisualStyle] = useState<string>('');
  const [veoLighting, setVeoLighting] = useState<string>('');
  const [veoCameraMovement, setVeoCameraMovement] = useState<string>('');
  const [veoMotionIntensity, setVeoMotionIntensity] = useState<string>('moderate');
  const [veoMood, setVeoMood] = useState<string>('');
  
  // Veo accordion states
  const [showVeoAudioAccordion, setShowVeoAudioAccordion] = useState<boolean>(false);
  const [showVeoVisualAccordion, setShowVeoVisualAccordion] = useState<boolean>(false);
  const [showVeoMotionAccordion, setShowVeoMotionAccordion] = useState<boolean>(false);
  const [showVeoSafetyAccordion, setShowVeoSafetyAccordion] = useState<boolean>(false);
  
  // Advanced Video Motion Controls State
  const [selectedMotionTemplate, setSelectedMotionTemplate] = useState<string>('');
  const [selectedMotionTypes, setSelectedMotionTypes] = useState<string[]>([]);
  const [motionIntensity, setMotionIntensity] = useState<string>('moderate');
  const [motionSpeed, setMotionSpeed] = useState<number>(1.0);
  
  // Accordion state for video controls
  const [showVideoMotionAccordion, setShowVideoMotionAccordion] = useState<boolean>(false);
  const [showVideoCameraAccordion, setShowVideoCameraAccordion] = useState<boolean>(false);
  const [showVideoOptimizationAccordion, setShowVideoOptimizationAccordion] = useState<boolean>(false);
  
  // Auto-optimization state
  const [isOptimizing, setIsOptimizing] = useState<boolean>(false);
  const [optimizationResult, setOptimizationResult] = useState<any>(null);
  const [showOptimizationResult, setShowOptimizationResult] = useState<boolean>(false);
  
  // Generated content state - Now supporting mixed provider results
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[] | null>(null);
  
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  
  // Video tab image source management
  const [videoImageSource, setVideoImageSource] = useState<'generated' | 'library' | 'upload'>('generated');
  const [generatedVideo, setGeneratedVideo] = useState<{
    url: string;
    duration: number;
    seed: number;
    prompt: string;
  } | null>(null);

  // Track if user manually removed the character image to prevent re-adding
  const characterImageRemovedRef = React.useRef(false);

  // Reset character image removed flag when modal opens
  React.useEffect(() => {
    if (isOpen) {
      characterImageRemovedRef.current = false;
    }
  }, [isOpen]);

  // Custom remove handler that tracks character image removal
  const handleRemoveReferenceImage = React.useCallback((index: number) => {
    const imageToRemove = referenceImages[index];
    
    // Check if the removed image is the character image
    if (initialImageUrl && imageToRemove?.uri === initialImageUrl) {
      console.log('🗑️ User manually removed character image - preventing re-add');
      characterImageRemovedRef.current = true;
    }
    
    removeReferenceImage(index);
  }, [referenceImages, initialImageUrl, removeReferenceImage]);

  // Auto-load character image as reference when provided for image tab
  React.useEffect(() => {
    if (isOpen && initialImageUrl && initialTab === 'image' && !characterImageRemovedRef.current) {
      console.log('🎭 Auto-loading character image as reference:', initialImageUrl);
      
      // Create a reference image object
      const characterReferenceImage: ReferenceImage = {
        uri: initialImageUrl,
        tag: 'character',
        url: initialImageUrl,
        name: 'Character Reference',
        uploadedAt: new Date().toISOString()
      };
      
      // Add to reference images if not already present
      const exists = referenceImages.some(img => img.uri === initialImageUrl);
      if (!exists) {
        console.log('✅ Character image added to reference images');
        addReferenceImage(characterReferenceImage);
      }
    }
  }, [isOpen, initialImageUrl, initialTab, addReferenceImage]);

  // Handle tab-based provider switching
  React.useEffect(() => {
    // Set initial provider if specified
    if (initialProvider && isOpen) {
      setActiveTab('video');
      setSelectedVideoProvider(initialProvider);
    } else if (activeTab === 'video' && !initialProvider) {
      setSelectedVideoProvider('runway');
    }
  }, [activeTab, initialProvider, isOpen]);

  // Handle provider changes - set appropriate defaults
  React.useEffect(() => {
    if (selectedVideoProvider === 'veo') {
      // Set Veo defaults
      setVideoDuration(8); // Fixed 8 seconds for Veo
      if (!['16:9', '9:16'].includes(videoAspectRatio)) {
        setVideoAspectRatio('16:9'); // Default to 16:9 for Veo
      }
    } else if (selectedVideoProvider === 'runway') {
      // Set Runway defaults
      if (videoDuration === 8) {
        setVideoDuration(5); // Default to 5 seconds for Runway
      }
    } else if (selectedVideoProvider === 'ltx') {
      // Set LTX defaults
      if (videoDuration === 8) {
        setVideoDuration(10); // Default to 10 seconds for LTX
      }
    }
  }, [selectedVideoProvider]);

  // Set up initial state when modal opens with existing image
  React.useEffect(() => {
    if (isOpen && initialImageUrl && initialTab === 'video') {
      setActiveTab('video');
      setGeneratedImages([{
        url: initialImageUrl,
        prompt: initialPrompt || 'Generated image',
        seed: 0,
        index: 0
      }]);
      setVideoImageSource('generated'); // Set to generated when auto-loading
      setVideoPrompt(initialPrompt ? `${initialPrompt}, cinematic movement, smooth motion` : 'Generate a video with smooth motion');
    } else if (isOpen && initialTab === 'image') {
      setActiveTab('image');
      if (initialPrompt) {
        setPrompt(initialPrompt);
      }
    } else if (isOpen && initialTab === 'prompt') {
      setActiveTab('prompt');
    } else if (isOpen && initialTab === 'upscale') {
      setActiveTab('upscale');
    }
  }, [isOpen, initialImageUrl, initialPrompt, initialTab]);
  
  const { showNotification } = useNotification();

  // Handle image generation - New Multi-API Logic
  const handleGenerateImage = async () => {
    const finalPrompt = buildFinalPrompt(prompt);
    
    if (!finalPrompt.trim()) {
      showNotification({
        title: 'Error',
        message: 'Please enter a prompt to generate an image',
        type: 'error'
      });
      return;
    }

    setIsGenerating(true);
    
    // Initialize loading slots based on NEW LOGIC
    let loadingSlots;
    let apiCalls = [];

    if (imageCount === 4) {
      // 4 COUNT: 2x Gemini Flash 2.5 + 1x SDXL + 1x Runway
      loadingSlots = [
        { url: '', prompt: finalPrompt, seed: 0, index: 0, provider: 'banana_nano' as const, loading: true, error: false },
        { url: '', prompt: finalPrompt, seed: 0, index: 1, provider: 'banana_nano' as const, loading: true, error: false },
        { url: '', prompt: finalPrompt, seed: 0, index: 2, provider: 'sdxl' as const, loading: true, error: false },
        { url: '', prompt: finalPrompt, seed: 0, index: 3, provider: 'runway' as const, loading: true, error: false }
      ];
      
      // Prepare API calls
      apiCalls = [
        { provider: 'banana_nano', count: 1, slotStart: 0 },
        { provider: 'banana_nano', count: 1, slotStart: 1 },
        { provider: 'sdxl', count: 1, slotStart: 2 },
        { provider: 'runway', count: 1, slotStart: 3 }
      ];
    } else if (imageCount === 2) {
      const hasReferenceImages = referenceImages.length > 0;
      
      if (hasReferenceImages) {
        // 2 COUNT + REFERENCE IMAGES: 2x Gemini Flash 2.5 only
        loadingSlots = [
          { url: '', prompt: finalPrompt, seed: 0, index: 0, provider: 'banana_nano' as const, loading: true, error: false },
          { url: '', prompt: finalPrompt, seed: 0, index: 1, provider: 'banana_nano' as const, loading: true, error: false }
        ];
        
        apiCalls = [
          { provider: 'banana_nano', count: 1, slotStart: 0 },
          { provider: 'banana_nano', count: 1, slotStart: 1 }
        ];
      } else {
        // 2 COUNT + NO REFERENCE IMAGES: 1x Gemini Flash 2.5 + 1x SDXL
        loadingSlots = [
          { url: '', prompt: finalPrompt, seed: 0, index: 0, provider: 'banana_nano' as const, loading: true, error: false },
          { url: '', prompt: finalPrompt, seed: 0, index: 1, provider: 'sdxl' as const, loading: true, error: false }
        ];
        
        apiCalls = [
          { provider: 'banana_nano', count: 1, slotStart: 0 },
          { provider: 'sdxl', count: 1, slotStart: 1 }
        ];
      }
    }

    setGeneratedImages(loadingSlots);
    setSelectedImageIndex(0);

    try {
      // Execute all API calls in parallel
      const promises = apiCalls.map(async ({ provider, count, slotStart }) => {
        return executeProviderCall(provider, count, slotStart, finalPrompt);
      });

      const results = await Promise.allSettled(promises);
      
      // Process results and show notifications
      const totalSuccess = results.reduce((sum, result) => 
        sum + (result.status === 'fulfilled' ? result.value : 0), 0);
      
      if (totalSuccess === imageCount) {
        // showNotification({
        //   title: 'Success',
        //   message: `Generated ${totalSuccess} images successfully!`,
        //   type: 'success'
        // });
      } else if (totalSuccess > 0) {
        showNotification({
          title: 'Partial Success',
          message: `Generated ${totalSuccess} out of ${imageCount} images.`,
          type: 'warning'
        });
      } else {
        showNotification({
          title: 'Generation Failed',
          message: 'Failed to generate any images. Please try again.',
          type: 'error'
        });
      }

    } catch (error) {
      console.error('💥 Image generation failed:', error);
      showNotification({
        title: 'Unexpected Error',
        message: 'Something went wrong during image generation. Please try again or contact support if the issue persists.',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Test generation function (admin only) - generates 1 Banana Nano image
  const handleTestGenerate = async () => {
    const finalPrompt = buildFinalPrompt(prompt);
    
    if (!finalPrompt.trim()) {
      showNotification({
        title: 'Error',
        message: 'Please enter a prompt to test generate an image',
        type: 'error'
      });
      return;
    }

    setIsTestGenerating(true);

    // Initialize single test slot
    const testSlot = {
      url: '',
      prompt: finalPrompt,
      seed: 0,
      index: 0,
      provider: 'banana_nano' as const,
      loading: true,
      error: false,
      errorMessage: ''
    };
    
    setGeneratedImages([testSlot]);
    setSelectedImageIndex(0);

    try {
      console.log('🧪 Admin Test: Calling Nano...');
      const success = await generateBananaNanoImage(finalPrompt);
      
      if (success) {
        // showNotification({
        //   title: 'Test Success',
        //   message: 'Image generated successfully!',
        //   type: 'success'
        // });
      } else {
        showNotification({
          title: 'Test Failed',
          message: 'Generation failed. Check console for details.',
          type: 'error'
        });
        // Reset on failure
        setGeneratedImages(null);
      }
    } catch (error) {
      console.error('💥 Test generation failed:', error);
      showNotification({
        title: 'Test Error',
        message: 'Test generation encountered an error',
        type: 'error'
      });
      setGeneratedImages(null);
    } finally {
      setIsTestGenerating(false);
    }
  };

  // New helper function to execute provider-specific calls
  const executeProviderCall = async (
    provider: 'banana_nano' | 'sdxl' | 'runway',
    count: number,
    slotStart: number,
    prompt: string
  ): Promise<number> => {
    try {
      let request;
      let response;
      
      switch (provider) {
        case 'banana_nano':
          request = {
            prompt: prompt,
            projectId: projectId,
            provider: 'banana_nano',
            vertex_ratio: selectedAspectRatio.split(' ')[0], // Extract ratio part
            num_images: count,
            reference_images: referenceImages.map(ref => ({
              uri: ref.url,
              tag: ref.name
            }))
          };
          response = await apiGenerateImage(request);
          break;

        case 'sdxl':
          const dimensions = getDimensionsForRatio(selectedAspectRatio);
          const qualitySettings = getQualitySettings(selectedQuality);
          
          request = {
            prompt: mapToSDXLParams(
              prompt,
              selectedImageStyle,
              selectedLighting,
              enhanceDetails,
              colorBoost,
              improveComposition
            ),
            projectId: projectId,
            provider: 'sdxl',
            count: count,
            width: dimensions.width,
            height: dimensions.height,
            steps: qualitySettings.steps,
            guidance_scale: qualitySettings.guidance_scale,
            negative_prompt: 'blurry, low quality, distorted, deformed faces, bad anatomy',
            reference_images: referenceImages.map(ref => ({
              uri: ref.url,
              tag: ref.name
            }))
          };
          response = await apiGenerateImage(request);
          break;

        case 'runway':
          request = {
            prompt: prompt,
            projectId: projectId,
            provider: 'runway',
            runway_ratio: mapAspectRatioToRunway(selectedAspectRatio.split(' ')[0]),
            num_images: count,
            reference_images: referenceImages.map(ref => ({
              uri: ref.url,
              tag: ref.name
            }))
          };
          response = await apiGenerateImage(request);
          break;

        default:
          throw new Error(`Unknown provider: ${provider}`);
      }

      // Update UI with results
      if (response.success) {
        let successCount = 0;
        
        if (response.data?.images?.length > 0) {
          // Handle multiple images (SDXL batch)
          response.data.images.forEach((img, imgIndex) => {
            const slotIndex = slotStart + imgIndex;
            if (slotIndex < imageCount) {
              updateImageSlot(slotIndex, {
                url: img.s3Url,
                seed: img.seed,
                loading: false,
                provider: provider
              });
              successCount++;
            }
          });
        } else if (response.data?.s3Url) {
          // Handle single image (Runway/Gemini)
          updateImageSlot(slotStart, {
            url: response.data.s3Url,
            seed: response.data.seed,
            loading: false,
            provider: provider
          });
          successCount = 1;
        }
        
        return successCount;
      } else {
        // Mark slots as failed with error message
        const errorMessage = response.message || 'Generation failed';
        for (let i = 0; i < count; i++) {
          updateImageSlot(slotStart + i, {
            loading: false,
            error: true,
            errorMessage: errorMessage
          });
        }
        console.error(`${provider} API returned failure:`, response);
        return 0;
      }
    } catch (error) {
      console.error(`${provider} generation failed:`, error);
      
      // Determine error message based on error type
      let errorMessage = 'Connection failed';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          errorMessage = 'Backend server is down';
        } else if (error.message.includes('HTTP error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Mark slots as failed with detailed error message
      for (let i = 0; i < count; i++) {
        updateImageSlot(slotStart + i, {
          loading: false,
          error: true,
          errorMessage: errorMessage
        });
      }
      return 0;
    }
  };

  // Helper function to update individual image slots
  const updateImageSlot = (index: number, updates: any) => {
    setGeneratedImages(prev => prev?.map((img, idx) => 
      idx === index ? { ...img, ...updates } : img
    ) || null);
  };

  // Helper function to get provider description for UI
  const getProviderDescription = () => {
    if (imageCount === 4) {
      return "4 Images: 2× Gemini Flash 2.5 + 1× SDXL + 1× Runway";
    } else if (imageCount === 2) {
      const hasReferenceImages = referenceImages.length > 0;
      if (hasReferenceImages) {
        return "2 Images + Reference: 2× Gemini Flash 2.5 only";
      } else {
        return "2 Images: 1× Gemini Flash 2.5 + 1× SDXL";
      }
    }
    return "Select image count to see provider distribution";
  };

  // Generate single Runway image
  const generateRunwayImage = async (finalPrompt: string): Promise<boolean> => {
    try {
      const runwayRequest = {
        prompt: finalPrompt,
        projectId: projectId,
        provider: 'runway' as const,
        runway_ratio: mapAspectRatioToRunway(selectedAspectRatio),
        runway_model: 'gen4_image',
        ...(referenceImages.length > 0 && {
          reference_images: referenceImages
        }),
        advanced_image: {
          style_preset: selectedImageStyle,
          lighting_style: selectedLighting,
          quality_preset: selectedImageQuality,
          enhance_details: enhanceDetails,
          color_boost: colorBoost,
          improve_composition: improveComposition
        }
      };

      console.log('🛩️ Calling  API...');
      const response = await apiGenerateImage(runwayRequest);
      
      if (response.success && response.data?.s3Url) {
        // Update slot 0 with Runway result
        setGeneratedImages(prev => prev?.map((img, idx) => 
          idx === 0 ? {
            ...img,
            url: response.data.s3Url,
            seed: response.data.seed,
            loading: false
          } : img
        ) || null);
        return true;
      } else {
        // Mark slot 0 as failed
        setGeneratedImages(prev => prev?.map((img, idx) => 
          idx === 0 ? { 
            ...img, 
            loading: false, 
            error: true,
            errorMessage: response.message || 'Generation failed'
          } : img
        ) || null);
        
        // showNotification({
        //   title: 'Premium Provider Failed',
        //   message: 'Failed to generate premium quality image. Standard images may still succeed.',
        //   type: 'warning'
        // });
        return false;
      }
    } catch (error) {
      console.error('Generation failed:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Generation failed';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          errorMessage = 'Backend server is down';
        } else if (error.message.includes('HTTP error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      setGeneratedImages(prev => prev?.map((img, idx) => 
        idx === 0 ? { 
          ...img, 
          loading: false, 
          error: true,
          errorMessage: errorMessage
        } : img
      ) || null);
      
      // Silent for individual provider - main handler will show notification
      return false;
    }
  };

  // Generate 3 SDXL images
  const generateSDXLImages = async (finalPrompt: string): Promise<number> => {
    try {
      const dimensions = getDimensionsForRatio(selectedAspectRatio);
      const qualitySettings = getSDXLQualitySettings(selectedImageQuality);
      const sdxlPrompt = mapToSDXLParams(
        finalPrompt,
        selectedImageStyle,
        selectedLighting,
        enhanceDetails,
        colorBoost,
        improveComposition
      );
      
      const sdxlRequest = {
        prompt: sdxlPrompt,
        projectId: projectId,
        provider: 'sdxl' as const,
        count: imageCount === 2 ? 1 : 3, // Dynamic count based on toggle selection
        width: dimensions.width,
        height: dimensions.height,
        steps: qualitySettings.steps,
        guidance_scale: qualitySettings.guidance,
        negative_prompt: 'blurry, low quality, distorted, deformed faces, bad anatomy',
        use_refiner: qualitySettings.steps > 50,
        refiner_steps: 20,
        // Include reference images if available
        ...(referenceImages.length > 0 && {
          reference_images: referenceImages
        })
      };

      console.log('⚡ Calling API...');
      const response = await apiGenerateImage(sdxlRequest);
      
      if (response.success && response.data?.images?.length > 0) {
        let successCount = 0;
        // Update SDXL slots (slots 1, 2, 3 for 4-image mode, or just slot 1 for 2-image mode)
        response.data.images.forEach((img, sdxlIndex) => {
          const slotIndex = sdxlIndex + 1; // Slots start from index 1
          const maxSlots = imageCount; // Max slots based on selected count
          if (slotIndex < maxSlots) { // Only update within the selected range
            setGeneratedImages(prev => prev?.map((prevImg, idx) => 
              idx === slotIndex ? {
                ...prevImg,
                url: img.s3Url,
                seed: img.seed,
                loading: false
              } : prevImg
            ) || null);
            successCount++;
          }
        });
        return successCount;
      } else {
        // Mark remaining SDXL slots as failed
        setGeneratedImages(prev => prev?.map((img, idx) => 
          idx >= 1 && idx < imageCount ? { 
            ...img, 
            loading: false, 
            error: true,
            errorMessage: response.message || 'Generation failed'
          } : img
        ) || null);
        
        showNotification({
          title: 'Standard Provider Failed',
          message: `Failed to generate ${imageCount - 1} standard quality images. Premium image may still succeed.`,
          type: 'warning'
        });
        return 0;
      }
    } catch (error) {
      console.error('Generation failed:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Generation failed';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          errorMessage = 'Backend server is down';
        } else if (error.message.includes('HTTP error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      setGeneratedImages(prev => prev?.map((img, idx) => 
        idx >= 1 && idx < imageCount ? { 
          ...img, 
          loading: false, 
          error: true,
          errorMessage: errorMessage
        } : img
      ) || null);
      
      // Silent for individual provider - main handler will show notification
      return 0;
    }
  };

  // Generate single Banana Nano image
  // NOTE: Function available for future integration - currently not called in main generation flow
  const generateBananaNanoImage = async (finalPrompt: string): Promise<boolean> => {
    try {
      const dimensions = getDimensionsForRatio(selectedAspectRatio);
      
      const bananaNanoRequest = {
        prompt: finalPrompt,
        projectId: projectId,
        provider: 'banana_nano' as const,
        width: dimensions.width,
        height: dimensions.height,
        banana_guidance_scale: 7.5,
        banana_num_inference_steps: 20,
        banana_scheduler: 'DPMSolverMultistepScheduler' as const,
        negative_prompt: 'blurry, low quality, distorted, ugly, bad anatomy',
        num_images: 1,
        ...(referenceImages.length > 0 && {
          reference_images: referenceImages
        })
      };

      console.log('🍌 Calling API...');
      const response = await apiGenerateImage(bananaNanoRequest);
      
      if (response.success && response.data?.s3Url) {
        // Update the appropriate slot with Banana Nano result
        // For test: slot 0, for integration: adjust slot as needed
        setGeneratedImages(prev => prev?.map((img, idx) => 
          idx === 0 ? {  // Use slot 0 for test, adjust during integration
            ...img,
            url: response.data.s3Url,
            seed: response.data.seed,
            loading: false
          } : img
        ) || null);
        return true;
      } else {
        // Mark slot as failed
        setGeneratedImages(prev => prev?.map((img, idx) => 
          idx === 0 ? {  
            ...img,
            loading: false,
            error: true,
            errorMessage: response.message || 'Generation failed'
          } : img
        ) || null);
        
        return false;
      }
    } catch (error) {
      console.error('Generation failed:', error);
      
      // Determine error message based on error type
      let errorMessage = 'Generation failed';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
          errorMessage = 'Backend server is down';
        } else if (error.message.includes('HTTP error')) {
          errorMessage = `Server error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Mark slot as failed with detailed error message
      setGeneratedImages(prev => prev?.map((img, idx) => 
        idx === 0 ? {  
          ...img,
          loading: false,
          error: true,
          errorMessage: errorMessage
        } : img
      ) || null);
      
      return false;
    }
  };

  // Handle video generation - REAL API CALL
  const handleGenerateVideo = async () => {
    const cleanVideoPrompt = videoPrompt.replace(/'/g, ""); // Remove apostrophes to prevent API errors
    const finalPrompt = cleanVideoPrompt.trim() || 'Generate a short video with smooth motion';
    
    if (!finalPrompt.trim()) {
      showNotification({
        title: 'Error',
        message: 'Please enter a prompt to generate a video',
        type: 'error'
      });
      return;
    }

    console.log('🎬 CHECKING VIDEO IMAGE SOURCES:');
    console.log('  videoImageSource:', videoImageSource);
    console.log('  generatedImages:', generatedImages?.length || 0, 'images');
    console.log('  referenceImages:', referenceImages.length, 'images');
    console.log('  selectedImageIndex:', selectedImageIndex);

    setIsGeneratingVideo(true);

    try {
      const selectedImage = getSelectedVideoImageForModal();
      console.log('🖼️ SELECTED VIDEO IMAGE:', selectedImage);
      
      if (!selectedImage) {
        console.error('❌ NO IMAGE FOUND - Debug info:');
        console.error('  videoImageSource:', videoImageSource);
        console.error('  generatedImages:', generatedImages);
        console.error('  referenceImages:', referenceImages);
        console.error('  selectedImageIndex:', selectedImageIndex);
        throw new Error('No image selected for video generation');
      }
      
      // Helper function to convert blob URL to base64
      const blobToBase64 = async (blobUrl: string): Promise<string> => {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };
      
      // Determine if we need to convert blob URL to base64
      let imageData: { imageBytes?: string; image_url?: string };
      
      if (selectedImage.url.startsWith('data:')) {
        // Already base64
        imageData = { imageBytes: selectedImage.url };
      } else if (selectedImage.url.startsWith('blob:')) {
        // Convert blob URL to base64
        console.log('🔄 Converting blob URL to base64...');
        const base64Data = await blobToBase64(selectedImage.url);
        imageData = { imageBytes: base64Data };
      } else {
        // S3 URL or other HTTP URL
        imageData = { image_url: selectedImage.url };
      }
      
      const request: GenerateVideoRequest = {
        projectId: projectId,
        provider: selectedVideoProvider, // Use selected provider
        ...imageData, // Use imageBytes for local/blob, image_url for S3
        prompt: finalPrompt,
        duration: videoDuration,
        aspect_ratio: videoAspectRatio,
        quality: videoQuality,
        
        // Provider-specific parameters
        ...(selectedVideoProvider === 'runway' && {
          advanced_runway: {
            motion_types: selectedMotionTypes,
            motion_intensity: motionIntensity,
            motion_speed: motionSpeed,
            motion_template: selectedMotionTemplate
          }
        }),
        
        ...(selectedVideoProvider === 'veo' && {
          veo_model: veoModel,
          negative_prompt: negativePrompt,
          person_generation: personGeneration,
          dialogue_cue: dialogueCue,
          advanced_veo: {
            visual_style: veoVisualStyle,
            lighting: veoLighting,
            camera_movement: veoCameraMovement,
            sound_effects: veoSoundEffects,
            ambient_noise: veoAmbientNoise,
            motion_intensity: veoMotionIntensity,
            mood: veoMood,
            audio_style: veoAudioStyle
          }
        })
      };

      console.log('🎬 MAKING REAL VIDEO API CALL TO:', getApiUrl('/video-generation/single'));
      console.log('📝 Request:', request);      
      const response = await generateVideo(request);
      
      console.log('✅ Video API Response:', response);
      
      if (response.success && response.data?.s3Url) {
        // Store the generated video details
        setGeneratedVideo({
          url: response.data.s3Url,
          duration: response.data.duration,
          seed: response.data.seed,
          prompt: finalPrompt
        });
        
        // showNotification({
        //   title: 'Success',
        //   message: 'Video generated successfully! Review and click Done to add to timeline.',
        //   type: 'success'
        // });
      } else {
        throw new Error(response.message || 'Failed to generate video');
      }
    } catch (error) {
      console.error('💥 Video generation failed:', error);
      showNotification({
        title: 'Video Generation Failed',
        message: error instanceof Error ? error.message : 'There was an error generating your video',
        type: 'error'
      });
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Handle auto-optimization for video parameters
  const handleAutoOptimize = async () => {
    if (!videoPrompt.trim()) {
      showNotification({
        title: 'Error',
        message: 'Please enter a video prompt first',
        type: 'error'
      });
      return;
    }

    setIsOptimizing(true);
    setOptimizationResult(null);
    setShowOptimizationResult(false);

    try {
      console.log('🤖 Starting auto-optimization for:', videoPrompt);

      // Build optimization request - use the same image selection logic as video generation
      const selectedImage = getSelectedVideoImage(
        videoImageSource,
        generatedImages,
        selectedImageIndex,
        referenceImages
      );
      
      console.log('🖼️ Selected image for optimization:', selectedImage);
      
      // Check if we have a data URI and handle it properly
      const imageUrl = selectedImage?.url || '';
      const isDataUri = imageUrl.startsWith('data:');
      
      const optimizationRequest = {
        prompt: videoPrompt.trim(),
        provider: 'runway', // Default to runway for optimization
        ...(isDataUri ? {
          // Extract base64 from data URI and send as imageBytes
          imageBytes: imageUrl.split(',')[1] // Get everything after 'data:image/webp;base64,'
        } : {
          // Use regular image URL
          image_url: imageUrl
        }),
        duration: videoDuration,
        aspect_ratio: videoAspectRatio,
        quality: videoQuality,
        // Include current advanced settings
        advanced_runway: {
          motion_types: selectedMotionTypes,
          motion_intensity: motionIntensity,
          motion_speed: motionSpeed,
          motion_template: selectedMotionTemplate
        }
      };

      console.log('📤 Optimization request:', optimizationRequest);

      const response = await fetch(getApiUrl('/video-generation/optimize'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('authToken') && {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          })
        },
        body: JSON.stringify(optimizationRequest)
      });

      if (!response.ok) {
        throw new Error(`Optimization failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Auto-optimization result:', result);

      if (result.success) {
        setOptimizationResult(result.data || result);
        setShowOptimizationResult(true);
        
        // showNotification({
        //   title: 'Success',
        //   message: `Analysis complete! Found ${result.data?.recommendations?.length || 0} optimization suggestions`,
        //   type: 'success'
        // });
      } else {
        throw new Error(result.message || 'Optimization failed');
      }

    } catch (error) {
      console.error('💥 Auto-optimization failed:', error);
      showNotification({
        title: 'Optimization Failed',
        message: error instanceof Error ? error.message : 'Failed to analyze video parameters',
        type: 'error'
      });
    } finally {
      setIsOptimizing(false);
    }
  };

  // Helper function to map Claude's optimization values to UI format
  const mapOptimizationToUI = (optimized: any) => {
    // Claude should now return values that match UI format directly
    // This function is kept for any legacy format conversion if needed
    console.log('🔄 Mapping optimization values (should be minimal now):', optimized);
    return optimized;
  };

  // Apply optimized parameters to form
  const applyOptimizedParams = (type: 'basic' | 'advanced' = 'advanced') => {
    console.log('🔧 APPLY BUTTON CLICKED - Debug Info:');
    console.log('  type:', type);
    console.log('  optimizationResult:', optimizationResult);
    
    // Get the appropriate optimization based on type
    let optimized;
    if (type === 'basic') {
      optimized = optimizationResult?.basic_optimization;
      console.log('  Using basic_optimization:', optimized);
    } else {
      optimized = optimizationResult?.advanced_optimization;
      console.log('  Using advanced_optimization:', optimized);
    }
    
    // Fallback to old structure if new structure not available
    if (!optimized) {
      optimized = optimizationResult?.optimized_dto || optimizationResult?.optimized_params;
      console.log('  Fallback to old structure:', optimized);
    }

    if (!optimized) {
      console.log('❌ No optimized data found, returning early');
      return;
    }

    // Map Claude's format to UI format
    const mappedOptimized = mapOptimizationToUI(optimized);
    console.log('  mapped optimized object:', mappedOptimized);
    
    // Apply basic parameters
    if (mappedOptimized.duration) {
      console.log(`🕐 Setting duration: ${mappedOptimized.duration}`);
      // Ensure duration doesn't exceed Runway's 5-second limit
      const validDuration = Math.min(mappedOptimized.duration, 5);
      if (validDuration !== mappedOptimized.duration) {
        console.warn(`⚠️ Duration ${mappedOptimized.duration}s exceeds limit, capped to ${validDuration}s`);
      }
      setVideoDuration(validDuration);
    }
    if (mappedOptimized.quality) {
      console.log(`🎨 Setting quality: ${mappedOptimized.quality}`);
      setVideoQuality(mappedOptimized.quality);
    }
    if (mappedOptimized.aspect_ratio) {
      console.log(`📐 Original aspect ratio: ${mappedOptimized.aspect_ratio}`);
      
      // Convert pixel ratios back to standard ratios if needed
      const standardRatio = mapRunwayRatioToStandard(mappedOptimized.aspect_ratio);
      console.log(`📐 Converted to standard ratio: ${standardRatio}`);
      console.log(`📐 Current videoAspectRatio before: ${videoAspectRatio}`);
      
      // Set the standard ratio for API usage
      setVideoAspectRatio(standardRatio);
      console.log(`📐 Called setVideoAspectRatio with: ${standardRatio}`);
    }
    
    // Apply enhanced prompt if available
    if (optimizationResult?.enhanced_prompt && type === 'advanced') {
      console.log(`📝 Setting enhanced prompt: ${optimizationResult.enhanced_prompt}`);
      setVideoPrompt(optimizationResult.enhanced_prompt);
    }
    
    // Apply advanced runway parameters (only for advanced type)
    if (type === 'advanced' && mappedOptimized.advanced_runway) {
      const advanced = mappedOptimized.advanced_runway;
      console.log('🚀 Applying advanced  parameters:', advanced);
      if (advanced.motion_types) {
        console.log(`🎬 Setting motion types: ${advanced.motion_types}`);
        setSelectedMotionTypes(advanced.motion_types);
      }
      if (advanced.motion_template) {
        console.log(`🎭 Setting motion template: ${advanced.motion_template}`);
        setSelectedMotionTemplate(advanced.motion_template);
      }
      if (advanced.motion_intensity) {
        console.log(`💪 Setting motion intensity: ${advanced.motion_intensity}`);
        setMotionIntensity(advanced.motion_intensity);
      }
      if (advanced.motion_speed) {
        console.log(`⚡ Setting motion speed: ${advanced.motion_speed}`);
        setMotionSpeed(advanced.motion_speed);
      }
      if (advanced.motion_template) {
        console.log(`🎭 Setting motion template: ${advanced.motion_template}`);
        setSelectedMotionTemplate(advanced.motion_template);
      }
    }

    console.log('✅ About to show notification');
    // showNotification({
    //   title: 'Applied',
    //   message: 'Optimized parameters have been applied to your settings',
    //   type: 'success'
    // });
    console.log('✅ Notification called');
    
    // Log current state after applying
    setTimeout(() => {
      console.log('📊 STATE AFTER APPLY (1 second later):');
      console.log(`  videoDuration: ${videoDuration}`);
      console.log(`  videoQuality: ${videoQuality}`);
      console.log(`  videoAspectRatio: ${videoAspectRatio}`);
      console.log(`  selectedMotionTypes: ${selectedMotionTypes}`);
      console.log(`  motionIntensity: ${motionIntensity}`);
      console.log(`  motionSpeed: ${motionSpeed}`);
      console.log(`  selectedMotionTemplate: ${selectedMotionTemplate}`);
    }, 1000);
  };

  // Copy optimization result to clipboard
  const copyOptimizationResult = async () => {
    if (!optimizationResult) return;
    
    const resultText = JSON.stringify(optimizationResult, null, 2);
    
    try {
      await navigator.clipboard.writeText(resultText);
      showNotification({
        title: 'Copied',
        message: 'Optimization result copied to clipboard',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to copy to clipboard',
        type: 'error'
      });
    }
  };

  // Reset modal state when closing
  const handleClose = () => {
    setActiveTab('image');
    setGeneratedImages(null);
    setGeneratedVideo(null);
    setSelectedImageIndex(0);
    setVideoImageSource('generated'); // Reset to default
    setPrompt('');
    setVideoPrompt('');
    onClose();
  };

  // Helper function to get the currently selected image for video generation
  const getSelectedVideoImageForModal = () => {
    console.log('🔍 getSelectedVideoImageForModal called with:');
    console.log('  videoImageSource:', videoImageSource);
    console.log('  generatedImages:', generatedImages?.length || 0);
    console.log('  referenceImages:', referenceImages.length);
    console.log('  selectedImageIndex:', selectedImageIndex);
    
    const result = getSelectedVideoImage(
      videoImageSource,
      generatedImages,
      selectedImageIndex,
      referenceImages
    );
    
    console.log('🖼️ getSelectedVideoImage returned:', result);
    return result;
  };

  // Handle prompt selection from PromptBrowser
  const handleUsePrompt = (promptText: string) => {
    setPrompt(promptText);
    // showNotification({
    //   title: 'Prompt Selected',
    //   message: 'Click "Go to Image Generation" to use this prompt for generating images',
    //   type: 'success'
    // });
  };

  // Handle using prompt for image generation
  const handleLoadForImage = (promptText: string, project?: any) => {
    console.log('🔍 handleLoadForImage called with:', { promptText: promptText?.slice(0, 50) + '...', project });
    
    setPrompt(promptText);
    setActiveTab('image');
    
    // Check if there's any character reference available
    let characterImageUrl = null;
    let characterId = null;
    
    if (project?.metadata?.characterRegistry) {
      const characterRegistry = project.metadata.characterRegistry;
      console.log('🔍 Character registry found:', characterRegistry);
      
      // Just get the first character with an image URL
      for (const [charId, charData] of Object.entries(characterRegistry) as [string, CharacterData][]) {
        if ((charData as CharacterData).imageUrl) {
          characterId = charId;
          characterImageUrl = (charData as CharacterData).imageUrl;
          console.log('🎭 Found character image:', { characterId, characterImageUrl });
          break;
        }
      }
    }
    
    if (characterImageUrl) {
      console.log('✅ Loading character reference image:', characterImageUrl);
      
      // Create a reference image object for the character
      const characterReferenceImage: ReferenceImage = {
        uri: characterImageUrl,
        tag: 'character',
        url: characterImageUrl,
        name: `Character Reference: ${characterId}`,
        uploadedAt: new Date().toISOString()
      };
      
      // Add to reference images if not already present
      const exists = referenceImages.some(img => img.uri === characterImageUrl);
      if (!exists) {
        console.log('✅ Character reference image loaded from prompt');
        addReferenceImage(characterReferenceImage);
      } else {
        console.log('ℹ️ Character reference image already exists');
      }
      
      showNotification({
        title: 'Prompt & Character Ready!',
        message: 'Prompt and character reference loaded for image generation',
        type: 'success'
      });
    } else {
      console.log('ℹ️ No character reference found in prompt');
      // showNotification({
      //   title: 'Prompt Ready!',
      //   message: 'Prompt ready for image generation',
      //   type: 'success'
      // });
    }
  };

  // Handle using prompt for video generation
  const handleLoadForVideo = (promptText: string, project?: any) => {
    console.log('🔍 handleLoadForVideo called with:', { promptText: promptText?.slice(0, 50) + '...', project });
    
    setVideoPrompt(promptText);
    setActiveTab('video');
    
    // Check if there's any character reference available
    let characterImageUrl = null;
    let characterId = null;
    
    if (project?.metadata?.characterRegistry) {
      const characterRegistry = project.metadata.characterRegistry;
      console.log('🔍 Character registry found for video:', characterRegistry);
      
      // Just get the first character with an image URL
      for (const [charId, charData] of Object.entries(characterRegistry) as [string, CharacterData][]) {
        if ((charData as CharacterData).imageUrl) {
          characterId = charId;
          characterImageUrl = (charData as CharacterData).imageUrl;
          console.log('🎭 Found character image for video:', { characterId, characterImageUrl });
          break;
        }
      }
    }
    
    if (characterImageUrl) {
      console.log('✅ Loading character reference image for video:', characterImageUrl);
      
      // Create a reference image object for the character
      const characterReferenceImage: ReferenceImage = {
        uri: characterImageUrl,
        tag: 'character',
        url: characterImageUrl,
        name: `Character Reference: ${characterId}`,
        uploadedAt: new Date().toISOString()
      };
      
      // Add to reference images if not already present
      const exists = referenceImages.some(img => img.uri === characterImageUrl);
      if (!exists) {
        console.log('✅ Character reference image loaded for video generation');
        addReferenceImage(characterReferenceImage);
      } else {
        console.log('ℹ️ Character reference image already exists for video');
      }
      
      // showNotification({
      //   title: 'Prompt & Character Ready!',
      //   message: 'Prompt and character reference loaded for video generation',
      //   type: 'success'
      // });
    } else {
      console.log('ℹ️ No character reference found in prompt for video');
      // showNotification({
      //   title: 'Prompt Ready!',
      //   message: 'Prompt ready for video generation',
      //   type: 'success'
      // });
    }
  };

  // Custom modal using HTML/CSS instead of Mantine Modal
  if (!isOpen) return null;
  
  return (
    <>
      {/* Add CSS for loading animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}>
      <div style={{
        backgroundColor: '#2a2a2a',
        padding: '30px',
        borderRadius: '12px',
        width: '800px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
        color: 'white'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '600', color: '#8b5cf6' }}>
            {t('title')}
          </h2>
          
          {/* Tab Navigation */}
          <div style={{ display: 'flex', borderRadius: '8px', backgroundColor: '#1a1a1a', padding: '4px' }}>
            <button
              onClick={() => setActiveTab('image')}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: activeTab === 'image' ? '#8b5cf6' : 'transparent',
                color: activeTab === 'image' ? 'white' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              {t('tabs.generateImage')}
            </button>
            {!disableVideoTab && (
              <button
                onClick={() => setActiveTab('video')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: activeTab === 'video' ? '#8b5cf6' : 'transparent',
                  color: activeTab === 'video' ? 'white' : '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('tabs.generateVideo')}
              </button>
            )}
            {!disablePromptsTab && (
              <button
                onClick={() => setActiveTab('prompt')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: activeTab === 'prompt' ? '#8b5cf6' : 'transparent',
                  color: activeTab === 'prompt' ? 'white' : '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('tabs.prompts')}
              </button>
            )}
            {!disableUpscaleTab && (
              <button
                onClick={() => setActiveTab('upscale')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: activeTab === 'upscale' ? '#8b5cf6' : 'transparent',
                  color: activeTab === 'upscale' ? 'white' : '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                {t('tabs.upscale')}
              </button>
            )}
          </div>
        </div>

        {/* IMAGE TAB */}
        {activeTab === 'image' && !generatedImages && (
          <ImageGenerationTab
            prompt={prompt}
            setPrompt={setPrompt}
            imageCount={imageCount}
            setImageCount={setImageCount}
            selectedProvider={selectedProvider}
            selectedQuality={selectedQuality}
            setSelectedQuality={setSelectedQuality}
            selectedAspectRatio={selectedAspectRatio}
            setSelectedAspectRatio={setSelectedAspectRatio}
            referenceImages={referenceImages}
            addReferenceImage={addReferenceImage}
            addFromLibrary={addFromLibrary}
            handleRemoveReferenceImage={handleRemoveReferenceImage}
            canAddMore={canAddMore}
            maxImages={maxImages}
            projectId={projectId}
            isGenerating={isGenerating}
            handleGenerateImage={handleGenerateImage}
            handleClose={handleClose}
            showImageStyleAccordion={showImageStyleAccordion}
            setShowImageStyleAccordion={setShowImageStyleAccordion}
            showImageEnhancementAccordion={showImageEnhancementAccordion}
            setShowImageEnhancementAccordion={setShowImageEnhancementAccordion}
            selectedImageStyle={selectedImageStyle}
            setSelectedImageStyle={setSelectedImageStyle}
            selectedLighting={selectedLighting}
            setSelectedLighting={setSelectedLighting}
            selectedImageQuality={selectedImageQuality}
            setSelectedImageQuality={setSelectedImageQuality}
            enhanceDetails={enhanceDetails}
            setEnhanceDetails={setEnhanceDetails}
            colorBoost={colorBoost}
            setColorBoost={setColorBoost}
            improveComposition={improveComposition}
            setImproveComposition={setImproveComposition}
          />
        )}

        {/* IMAGE RESULTS GRID */}
        {activeTab === 'image' && generatedImages && (
          <ImageResultsDisplay
            generatedImages={generatedImages}
            selectedImageIndex={selectedImageIndex}
            setSelectedImageIndex={setSelectedImageIndex}
            onGenerateNewSet={() => {
              setGeneratedImages(null);
              setSelectedImageIndex(0);
            }}
            onGenerateVideoFromImage={() => {
              const selectedImage = generatedImages[selectedImageIndex];
              if (selectedImage && selectedImage.url && !selectedImage.error && !selectedImage.loading) {
                setActiveTab('video');
                setVideoImageSource('generated');
                setVideoPrompt(`${selectedImage.prompt}, cinematic movement, smooth motion, gentle animation`);
              }
            }}
            onUseSelectedImage={() => {
              const selectedImage = generatedImages[selectedImageIndex];
              if (selectedImage && selectedImage.url && !selectedImage.error && !selectedImage.loading) {
                onImageGenerated(selectedImage.url, selectedImage.prompt);
                handleClose();
              }
            }}
            disableVideoTab={disableVideoTab}
          />
        )}

        {/* VIDEO TAB - Using new VideoTab component */}
        {activeTab === 'video' && !generatedVideo && (
          <VideoTab
            videoImageSource={videoImageSource}
            setVideoImageSource={setVideoImageSource}
            generatedImages={generatedImages}
            setGeneratedImages={setGeneratedImages}
            selectedImageIndex={selectedImageIndex}
            referenceImages={referenceImages}
            addReferenceImage={addReferenceImage}
            handleRemoveReferenceImage={handleRemoveReferenceImage}
            addFromLibrary={addFromLibrary}
            canAddMore={canAddMore}
            videoPrompt={videoPrompt}
            setVideoPrompt={setVideoPrompt}
            videoDuration={videoDuration}
            setVideoDuration={setVideoDuration}
            videoQuality={videoQuality}
            setVideoQuality={setVideoQuality}
            videoAspectRatio={videoAspectRatio}
            setVideoAspectRatio={setVideoAspectRatio}
            selectedVideoProvider={selectedVideoProvider}
            setSelectedVideoProvider={setSelectedVideoProvider}
            selectedMotionTemplate={selectedMotionTemplate}
            setSelectedMotionTemplate={setSelectedMotionTemplate}
            selectedMotionTypes={selectedMotionTypes}
            setSelectedMotionTypes={setSelectedMotionTypes}
            motionIntensity={motionIntensity}
            setMotionIntensity={setMotionIntensity}
            motionSpeed={motionSpeed}
            setMotionSpeed={setMotionSpeed}
            isOptimizing={isOptimizing}
            optimizationResult={optimizationResult}
            showOptimizationResult={showOptimizationResult}
            setShowOptimizationResult={setShowOptimizationResult}
            handleAutoOptimize={handleAutoOptimize}
            applyOptimizedParams={applyOptimizedParams}
            copyOptimizationResult={copyOptimizationResult}
            showVideoMotionAccordion={showVideoMotionAccordion}
            setShowVideoMotionAccordion={setShowVideoMotionAccordion}
            isGeneratingVideo={isGeneratingVideo}
            handleGenerateVideo={handleGenerateVideo}
            getSelectedVideoImage={getSelectedVideoImageForModal}
            projectId={projectId}
            isAdmin={isAdmin}
            setActiveTab={setActiveTab}
            onCancel={onClose}
            
            // Veo-specific props
            veoModel={veoModel}
            onVeoModelChange={setVeoModel}
            dialogueCue={dialogueCue}
            onDialogueCueChange={setDialogueCue}
            veoSoundEffects={veoSoundEffects}
            onVeoSoundEffectsChange={setVeoSoundEffects}
            veoAmbientNoise={veoAmbientNoise}
            onVeoAmbientNoiseChange={setVeoAmbientNoise}
            veoAudioStyle={veoAudioStyle}
            onVeoAudioStyleChange={setVeoAudioStyle}
            veoVisualStyle={veoVisualStyle}
            onVeoVisualStyleChange={setVeoVisualStyle}
            veoLighting={veoLighting}
            onVeoLightingChange={setVeoLighting}
            veoCameraMovement={veoCameraMovement}
            onVeoCameraMovementChange={setVeoCameraMovement}
            veoMotionIntensity={veoMotionIntensity}
            onVeoMotionIntensityChange={setVeoMotionIntensity}
            veoMood={veoMood}
            onVeoMoodChange={setVeoMood}
            personGeneration={personGeneration}
            onPersonGenerationChange={setPersonGeneration}
            negativePrompt={negativePrompt}
            onNegativePromptChange={setNegativePrompt}
            
            // Veo accordion states
            showVeoAudioAccordion={showVeoAudioAccordion}
            onToggleVeoAudioAccordion={() => setShowVeoAudioAccordion(!showVeoAudioAccordion)}
            showVeoVisualAccordion={showVeoVisualAccordion}
            onToggleVeoVisualAccordion={() => setShowVeoVisualAccordion(!showVeoVisualAccordion)}
            showVeoMotionAccordion={showVeoMotionAccordion}
            onToggleVeoMotionAccordion={() => setShowVeoMotionAccordion(!showVeoMotionAccordion)}
            showVeoSafetyAccordion={showVeoSafetyAccordion}
            onToggleVeoSafetyAccordion={() => setShowVeoSafetyAccordion(!showVeoSafetyAccordion)}
          />
        )}

        {/* VIDEO RESULT - Using new VideoResult component */}
        {activeTab === 'video' && generatedVideo && (
          <VideoResult
            generatedVideo={generatedVideo}
            onGenerateNew={() => {
              setGeneratedVideo(null);
              // Stay on video tab to regenerate
            }}
            onUseVideo={(videoUrl: string, prompt: string, duration: number) => {
              if (onVideoGenerated) {
                onVideoGenerated(videoUrl, prompt, duration);
              }
            }}
            onClose={handleClose}
          />
        )}

        {/* PROMPT TAB */}
        {activeTab === 'prompt' && (
          <PromptBrowserTab
            projectId={projectId}
            onPromptSelect={handleUsePrompt}
            onLoadForImage={handleLoadForImage}
            onLoadForVideo={handleLoadForVideo}
            onClose={handleClose}
          />
        )}

        {/* UPSCALE TAB */}
        {activeTab === 'upscale' && (
          <UpscaleTab 
            projectId={projectId}
            initialVideoUrl={initialVideoUrl}
            onUpscaleComplete={onVideoUpscaled}
            onCancel={onClose}
            onUseVideo={onVideoGenerated}
          />
        )}

      </div>
    </div>
    </>
  );
};

export default AIImageModalFresh;